import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/Layout';

const API = '/api';
const getToken = () => localStorage.getItem('lemai_token') || '';
const authHdr = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` });

// ─── Types ────────────────────────────────────────────────────────────────────

interface HealthStatus {
  vpn:       { status: string };
  armorip:   { status: string; last_alarm_ago_seconds: number | null; last_alarm_at?: string };
  protege:   { status: string };
  database:  { status: string };
  services:  Record<string, string>;
  checked_at: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function StatusDot({ ok, warn }: { ok: boolean; warn?: boolean }) {
  const color = ok ? '#22c55e' : warn ? '#f59e0b' : '#ef4444';
  return (
    <span style={{
      display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
      background: color, boxShadow: `0 0 6px ${color}88`, flexShrink: 0,
    }} />
  );
}

function ServiceBadge({ status }: { status: string }) {
  const ok = status === 'active';
  const color = ok ? '#22c55e' : status === 'failed' ? '#ef4444' : '#f59e0b';
  return (
    <span style={{
      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: `${color}22`, color, border: `1px solid ${color}44`,
    }}>
      {status}
    </span>
  );
}

function fmtAge(seconds: number | null): string {
  if (seconds === null) return '—';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

const SERVICE_LABELS: Record<string, string> = {
  'lemai':           'Lemai (main app)',
  'lemai-armorip':   'ArmorIP Poller',
  'lemai-tcp':       'TCP Alarm Listener',
  'armorip-relay':   'ArmorIP Relay',
  'openvpn@lemai':   'OpenVPN (lemai)',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function SystemHealth() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus]         = useState<HealthStatus | null>(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [toast, setToast]           = useState<{ msg: string; ok: boolean } | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [restarting, setRestarting] = useState<string | null>(null);
  const [testingAlarm, setTestingAlarm] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (user && !(user as any).is_super_admin) navigate('/dashboard');
  }, [user, navigate]);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/system-health/status`, { headers: authHdr() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setStatus(d);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    intervalRef.current = setInterval(fetchStatus, 30_000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [fetchStatus]);

  const handleRestart = async (service: string) => {
    setConfirming(null);
    setRestarting(service);
    try {
      const r = await fetch(`${API}/system-health/restart/${encodeURIComponent(service)}`, {
        method: 'POST', headers: authHdr(),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`${service} restarted successfully`, true);
        setTimeout(fetchStatus, 3000);
      } else {
        showToast(`Restart failed: ${d.output || 'unknown error'}`, false);
      }
    } catch (e: any) {
      showToast(`Error: ${e.message}`, false);
    } finally {
      setRestarting(null);
    }
  };

  const handleTestAlarm = async () => {
    setTestingAlarm(true);
    try {
      const r = await fetch(`${API}/system-health/test-alarm`, {
        method: 'POST', headers: authHdr(),
      });
      const d = await r.json();
      if (r.ok && d.ok) {
        showToast('Test alarm injected — check SOC Dashboard', true);
      } else {
        showToast(`Failed: ${d.error || 'unknown'}`, false);
      }
    } catch (e: any) {
      showToast(`Error: ${e.message}`, false);
    } finally {
      setTestingAlarm(false);
    }
  };

  function armorOk(): boolean {
    if (!status) return false;
    const s = status.armorip.last_alarm_ago_seconds;
    return s !== null && s <= 300;
  }
  function armorWarn(): boolean {
    if (!status) return false;
    const s = status.armorip.last_alarm_ago_seconds;
    return s !== null && s > 300 && s <= 900;
  }

  const cardStyle: React.CSSProperties = {
    background: '#111827', border: '1px solid #1a1a1a',
    borderRadius: 12, padding: '18px 20px', flex: 1, minWidth: 180,
  };
  const labelStyle: React.CSSProperties = {
    fontSize: 11, color: '#555555', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8,
  };
  const valueStyle: React.CSSProperties = {
    fontSize: 15, fontWeight: 700, color: '#e5e5e5',
    display: 'flex', alignItems: 'center', gap: 8,
  };

  return (
    <Layout title="System Health">
      <div style={{ padding: '28px 32px', maxWidth: 960, margin: '0 auto' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#f1f5f9', margin: 0 }}>System Health</h1>
            {status && (
              <p style={{ fontSize: 12, color: '#555555', margin: '4px 0 0' }}>
                Last updated: {new Date(status.checked_at).toLocaleTimeString()}
              </p>
            )}
          </div>
          <button
            onClick={fetchStatus}
            style={{ padding: '8px 16px', background: '#111111', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#a0a0a0', fontSize: 13, cursor: 'pointer', fontWeight: 500 }}
          >
            ↻ Refresh
          </button>
        </div>

        {error && (
          <div style={{ background: '#7f1d1d22', border: '1px solid #ef444433', borderRadius: 8, padding: '12px 16px', color: '#fca5a5', fontSize: 13, marginBottom: 20 }}>
            {error}
          </div>
        )}

        {/* Component Status Cards */}
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 28 }}>

          {/* VPN */}
          <div style={cardStyle}>
            <div style={labelStyle}>VPN</div>
            {loading ? <div style={{ ...valueStyle, color: '#555555' }}>Checking…</div> : (
              <div style={valueStyle}>
                <StatusDot ok={status?.vpn.status === 'up'} />
                {status?.vpn.status?.toUpperCase() ?? '—'}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#555555', marginTop: 6 }}>172.26.16.254</div>
          </div>

          {/* ArmorIP */}
          <div style={cardStyle}>
            <div style={labelStyle}>ArmorIP</div>
            {loading ? <div style={{ ...valueStyle, color: '#555555' }}>Checking…</div> : (
              <>
                <div style={valueStyle}>
                  <StatusDot ok={armorOk()} warn={armorWarn()} />
                  {status?.armorip.status === 'no_data' ? 'No Data' : status?.armorip.status?.toUpperCase()}
                </div>
                <div style={{ fontSize: 11, color: '#555555', marginTop: 6 }}>
                  Last alarm: {fmtAge(status?.armorip.last_alarm_ago_seconds ?? null)}
                </div>
              </>
            )}
          </div>

          {/* Protege GX */}
          <div style={cardStyle}>
            <div style={labelStyle}>Protege GX</div>
            {loading ? <div style={{ ...valueStyle, color: '#555555' }}>Checking…</div> : (
              <div style={valueStyle}>
                <StatusDot ok={status?.protege.status === 'connected'} />
                {status?.protege.status?.toUpperCase() ?? '—'}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#555555', marginTop: 6 }}>Port 8040</div>
          </div>

          {/* Database */}
          <div style={cardStyle}>
            <div style={labelStyle}>Database</div>
            {loading ? <div style={{ ...valueStyle, color: '#555555' }}>Checking…</div> : (
              <div style={valueStyle}>
                <StatusDot ok={status?.database.status === 'connected'} />
                {status?.database.status === 'connected' ? 'CONNECTED' : 'ERROR'}
              </div>
            )}
            <div style={{ fontSize: 11, color: '#555555', marginTop: 6 }}>PostgreSQL</div>
          </div>

        </div>

        {/* Service Table */}
        <div style={{ background: '#111827', border: '1px solid #1a1a1a', borderRadius: 12, overflow: 'hidden', marginBottom: 24 }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #1a1a1a' }}>
            <h2 style={{ fontSize: 14, fontWeight: 600, color: '#a0a0a0', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Services</h2>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ fontSize: 11, color: '#555555', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                <th style={{ padding: '10px 20px', textAlign: 'left', fontWeight: 600 }}>Service</th>
                <th style={{ padding: '10px 20px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                <th style={{ padding: '10px 20px', textAlign: 'right', fontWeight: 600 }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {['lemai', 'lemai-armorip', 'lemai-tcp', 'armorip-relay', 'openvpn@lemai'].map((svc, i) => (
                <tr key={svc} style={{ borderTop: i > 0 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
                  <td style={{ padding: '12px 20px', fontSize: 13, fontWeight: 500, color: '#e5e5e5' }}>
                    {SERVICE_LABELS[svc] ?? svc}
                    <span style={{ display: 'block', fontSize: 11, color: '#555555', marginTop: 2 }}>{svc}</span>
                  </td>
                  <td style={{ padding: '12px 20px' }}>
                    {loading ? <span style={{ fontSize: 12, color: '#555555' }}>—</span> : (
                      <ServiceBadge status={status?.services[svc] ?? 'unknown'} />
                    )}
                  </td>
                  <td style={{ padding: '12px 20px', textAlign: 'right' }}>
                    <button
                      disabled={restarting === svc}
                      onClick={() => setConfirming(svc)}
                      style={{
                        padding: '6px 14px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                        cursor: restarting === svc ? 'not-allowed' : 'pointer',
                        background: restarting === svc ? '#111111' : '#1d4ed822',
                        border: '1px solid #3b82f644',
                        color: restarting === svc ? '#555555' : '#60a5fa',
                      }}
                    >
                      {restarting === svc ? 'Restarting…' : svc === 'openvpn@lemai' ? 'Restart VPN' : 'Restart'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Test Alarm */}
        <div style={{ background: '#111827', border: '1px solid #1a1a1a', borderRadius: 12, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#e5e5e5' }}>Inject Test Alarm</div>
            <div style={{ fontSize: 12, color: '#555555', marginTop: 3 }}>Creates a test alarm visible in the SOC Dashboard</div>
          </div>
          <button
            disabled={testingAlarm}
            onClick={handleTestAlarm}
            style={{
              padding: '9px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: testingAlarm ? 'not-allowed' : 'pointer',
              background: testingAlarm ? '#111111' : '#065f4622',
              border: '1px solid #22c55e44',
              color: testingAlarm ? '#555555' : '#22c55e',
            }}
          >
            {testingAlarm ? 'Injecting…' : '⚡ Inject Test Alarm'}
          </button>
        </div>

      </div>

      {/* Confirmation Dialog */}
      {confirming && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
          <div style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 14, padding: 28, maxWidth: 380, width: '90%' }}>
            <h3 style={{ color: '#f1f5f9', fontSize: 16, fontWeight: 700, margin: '0 0 10px' }}>Restart {confirming}?</h3>
            <p style={{ color: '#a0a0a0', fontSize: 13, margin: '0 0 22px', lineHeight: 1.5 }}>
              This may cause a brief interruption to this service. Are you sure?
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirming(null)}
                style={{ padding: '8px 18px', borderRadius: 7, fontSize: 13, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: '#a0a0a0', fontWeight: 500 }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleRestart(confirming)}
                style={{ padding: '8px 18px', borderRadius: 7, fontSize: 13, cursor: 'pointer', background: '#dc262622', border: '1px solid #ef444433', color: '#f87171', fontWeight: 600 }}
              >
                Restart
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 28, right: 28, zIndex: 99999,
          background: toast.ok ? '#052e1622' : '#450a0a22',
          border: `1px solid ${toast.ok ? '#22c55e55' : '#ef444455'}`,
          color: toast.ok ? '#4ade80' : '#f87171',
          borderRadius: 10, padding: '12px 18px', fontSize: 13, fontWeight: 500,
          backdropFilter: 'blur(8px)', boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
          maxWidth: 360,
        }}>
          {toast.msg}
        </div>
      )}
    </Layout>
  );
}
